import * as React from 'react';
// @ts-ignore
import { CSSTransitionGroup } from 'react-transition-group';
import { debounce } from 'lodash';
import cx from 'classnames';
import { Query } from 'react-apollo';

import Button from 'components/ButtonDeprecated';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import EventTracking from 'lib/EventTracking';
import Heading from 'components/Heading';
import LayerTrigger from 'components/LayerTrigger';
import Row from 'components/Row';
import ScrollContainer from 'components/ScrollContainer';
import SearchBlock from 'components/SearchBlock';
import { SEARCH_NETWORK_LIMIT_MS } from 'chat_v2/constants/ChatConstants';
// @ts-ignore -going to be replaced soon-
import RecipientItem from './RecipientItem';
// @ts-ignore -going to be replaced soon-
import RecipientSelectionItem from './RecipientSelectionItem';
import Tooltip from 'components/Tooltip';
import { MessageType } from './SplitComposer';
import { UIEventContext } from './ComposerContext';
import MetricTracking, { MetricName, MetricTagValue, boolTagValue } from 'lib/MetricTracking';
import COMPOSER_RECIPIENTS_QUERY from 'chat_v2/components/SplitComposer/queries/ComposerRecipientsQuery.gql';
import { ComposerRecipients, ComposerRecipientsVariables, ComposerRecipients_composerRecipients_recipients, ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity } from 'chat_v2/components/SplitComposer/queries/__generated__/ComposerRecipients';
import { ComposerMessageType } from 'globalTypes';
import COUNT_RECIPIENTS_QUERY from 'queries/RecipientCount.gql';
import { QueryCountRecipients, QueryCountRecipientsVariables } from 'queries/__generated__/QueryCountRecipients';
import { MessageRecipientsRole } from 'globalTypes';

import '../SplitComposer.scss';

type Props = {
  onContinue: Function;
  messageType: MessageType;
  isUrgentMessage: boolean;
  uiEventContext: UIEventContext;
  selection: { [uuid: string]: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity };
  filters: { [recipientUuid: string]: { [filterType: string]: string[] } };
};

type State = {
  totalCharacterMutations: number;
  query: string;
};

export function DisabledTooltipLayer(props: { text: any; children: React.ReactNode }) {
  return (
    <LayerTrigger layer={<Tooltip>{props.text}</Tooltip>} placement="bottom" alignment="middle">
      <div>{props.children}</div>
    </LayerTrigger>
  );
}

class SelectFromList extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      totalCharacterMutations: 0,
      query: '',
    };
  }

  componentDidMount() {
    EventTracking.trackKey('announcements.share_to.view', this.props.uiEventContext);
  }

  _searchBlock?: { handleClose: () => void };

  handleQueryChange = debounce(
    (rawQuery: string = '') => {
      const query = rawQuery.trim();
      this.setState({ query, totalCharacterMutations: this.state.totalCharacterMutations + 1 });
    },
    SEARCH_NETWORK_LIMIT_MS,
    { leading: false, trailing: true }
  );

  renderHeader = (title: string, options: { disabled?: boolean; hidden: boolean }) => {
    if (options.hidden) {
      return null;
    } else {
      return (
        <Heading
          align="left"
          level="3"
          className={cx('SelectFromList-resultHeader', {
            // prettier-ignore
            'SelectFromList-resultHeader--disabled': options.disabled
          })}>
          {title}
        </Heading>
      );
    }
  };

  renderResults(results: ComposerRecipients_composerRecipients_recipients[], selection: { [uuid: string]: ComposerRecipients_composerRecipients_recipients_ComposerRecipientListEntity }) {
    return (
      <React.Fragment>
        {results.map(result => {
          if (result.__typename === 'TextHeader') {
            return (
              this.renderHeader(result.text, { disabled: false, hidden: false })
            )
          } else {
            return (
              <ul>
                <RecipientItem
                  key={result.recipient.uuid}
                  entity={result}
                  recipientIsSelected={result.recipient.uuid in selection}
                  selectionLength={Object.keys(selection).length}
                  districtIsSelected={false /* TODO fix */}
                />
              </ul>
            )
          }
        })}
      </React.Fragment>
    );
  };

  render() {
    const {
      selection,
      filters,
      uiEventContext,
    } = this.props;

    return (
      <React.Fragment>
        <div className="SelectFromList-main">
          <SearchBlock
            ref={ref => {
              if (ref && ref.handleClose) {
                this._searchBlock = ref;
              }
            }}
            className="SelectFromList-main-search"
            label="Search for people or classes"
            onDismiss={this.handleQueryChange}
            onQueryChange={this.handleQueryChange}
            onClick={() =>
              EventTracking.trackKey('announcements.share_to.search.click', uiEventContext)
            }
          />
          <ScrollContainer>
            <Query<ComposerRecipients, ComposerRecipientsVariables>
              query={COMPOSER_RECIPIENTS_QUERY}
              variables={{ type: ComposerMessageType.announcement, query: this.state.query }}
            >
              {announcementRecipientsResponse => {
                const { data: announcementRecipientsData, loading: announcementRecipientsLoading, error: announcementRecipientsError } = announcementRecipientsResponse;
                return (
                  <Query<ComposerRecipients, ComposerRecipientsVariables>
                    query={COMPOSER_RECIPIENTS_QUERY}
                    variables={{ type: ComposerMessageType.conversation, query: this.state.query }}
                  >
                    {conversationRecipientsResponse => {
                      const { data: conversationRecipientsData, loading: conversationRecipientsLoading, error: conversationRecipientsError } = conversationRecipientsResponse;
                      if (announcementRecipientsLoading || conversationRecipientsLoading) {
                        return <CenteredLoadingIndicator/>;
                      }

                      if ((announcementRecipientsError || conversationRecipientsError) || (!announcementRecipientsData || !conversationRecipientsData)) {
                        return null;
                      }

                      const announcementRecipientsPeopleHeaderIndex = announcementRecipientsData.composerRecipients.recipients.findIndex(entity => entity.__typename === 'TextHeader' && entity.text === 'People');

                      const results = (() => {
                        if (announcementRecipientsPeopleHeaderIndex > 0) {
                          // Both queries contain 'People' results, remove them from the first set of results to de-dupe & maintain the right order.
                          return [
                            ...announcementRecipientsData.composerRecipients.recipients.slice(0, announcementRecipientsPeopleHeaderIndex), 
                            ...conversationRecipientsData.composerRecipients.recipients
                          ]
                        } else {
                          return announcementRecipientsData.composerRecipients.recipients;
                        }
                      })();

                      return this.renderResults(results, selection);
                    }}
                  </Query>
                );
              }}
            </Query>
          </ScrollContainer>
        </div>
        <div className="SelectFromList-sidebar">
          <ScrollContainer>
            <React.Fragment>
              <Row size="xsmall">
                <Heading level="4">Selected</Heading>
              </Row>
              {Object.values(selection).length ? (
                <ul>
                  <CSSTransitionGroup
                    transitionName="SelectFromList-selectionItem-transition"
                    transitionEnterTimeout={200}
                    transitionLeaveTimeout={200}>
                    {
                      Object.values(selection).map(entity => (
                        <Query<QueryCountRecipients, QueryCountRecipientsVariables>
                          query={COUNT_RECIPIENTS_QUERY}
                          variables={{
                            input: {
                              type: entity.recipient.type,
                              uuid: entity.recipient.uuid,
                              filters: [{
                                roles: filters[entity.recipient.uuid].roles as MessageRecipientsRole[]
                              }],
                            }
                          }}
                        >
                        {({ data, error, loading }) => {
                          return (
                            <RecipientSelectionItem
                              key={entity.recipient.uuid}
                              entity={entity}
                              filters={filters[entity.recipient.uuid]}
                              count={data?.countRecipients?.count}
                            />
                          )
                        }}
                        </Query>
                      ))
                    }
                  </CSSTransitionGroup>
                </ul>
              ) : null}
            </React.Fragment>
          </ScrollContainer>
          <div className="SelectFromList-sidebar-footer">
            <Button
              onClick={() => {
                EventTracking.trackKey('announcements.share_to.next.click', uiEventContext);
                MetricTracking.increment('composer.search.view.ctr.success', {
                  tags: {
                    is_urgent: boolTagValue(this.props.isUrgentMessage),
                    did_type_search_query: boolTagValue(this.state.totalCharacterMutations > 0),
                  },
                });
                MetricTracking.count(
                  'composer.search.view.ctr.success.character_count',
                  this.state.totalCharacterMutations,
                  {
                    tags: {
                      is_urgent: boolTagValue(this.props.isUrgentMessage),
                    },
                  }
                );

                this.props.onContinue();
              }}
              disabled={!Object.keys(selection).length}
              data-test-selector="split-composer-continue-button">
              Continue
            </Button>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default SelectFromList;
